import React from 'react';
import { FaRocket, FaBuilding, FaChartLine, FaHandshake } from 'react-icons/fa';

const WhoWeWorkWith: React.FC = () => {
  const clientTypes = [
    {
      icon: <FaRocket className="text-4xl text-blue-600" />,
      title: 'Startups (Seed → Series B)',
      description: 'Fast-moving product teams that need to build MVPs, validate markets, and scale rapidly without compromising quality.',
      highlights: [
        'Rapid prototyping and MVP development',
        'Architecture designed for scale from day one',
        'Flexible engagement as funding stages evolve',
        'Technical due diligence support for investors'
      ]
    },
    {
      icon: <FaBuilding className="text-4xl text-blue-600" />,
      title: 'SMBs & Scale-ups',
      description: 'Established businesses modernizing legacy systems or building new digital products to compete in evolving markets.',
      highlights: [
        'Legacy system migration and modernization',
        'Digital transformation initiatives',
        'Custom business applications',
        'Integration with existing enterprise systems'
      ]
    },
    {
      icon: <FaChartLine className="text-4xl text-blue-600" />,
      title: 'Enterprise Product Teams',
      description: 'Large organizations that need specialized expertise to augment internal teams or tackle complex technical challenges.',
      highlights: [
        'Staff augmentation with senior engineers',
        'Microservices architecture implementation',
        'Cloud migration and optimization',
        'Performance and scalability improvements'
      ]
    },
    {
      icon: <FaHandshake className="text-4xl text-blue-600" />,
      title: 'Agencies & Partners',
      description: 'Creative agencies and consultancies seeking reliable engineering partners to deliver technical solutions for their clients.',
      highlights: [
        'White-label development services',
        'Technical project rescue and recovery',
        'Long-term partnership models',
        'Transparent communication and collaboration'
      ]
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Who We Partner With
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We collaborate with product teams, founders, and engineering managers who need reliable development partners — not freelancers.
            Our clients value predictability, transparency, and long-term strategic thinking.
          </p>
        </div>

        {/* Client Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {clientTypes.map((client, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 bg-blue-50 p-4 rounded-lg">
                  {client.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {client.title}
                  </h3>
                  <p className="text-gray-600">
                    {client.description}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {client.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-1.5 h-1.5 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-700">{highlight}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">
            Not sure if we're the right fit?
          </h3>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            We work with companies at various stages — from validating an idea to scaling a platform handling millions of users.
            Let's discuss your specific challenges and see how we can help.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg"
          >
            Schedule a Discovery Call
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhoWeWorkWith;
